import type { ConnectionPhase } from '@parsleyai/ui'
import { collection, type DocumentData, onSnapshot, type QuerySnapshot } from 'firebase/firestore'
import { createContext, type ReactNode, useEffect, useReducer } from 'react'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'
import type { StoredMeal } from '@/types/mealPlan'
import { initialMealLibraryState, mealLibraryReducer } from './mealLibraryReducer'
import { MealLibraryActionTypes, type MealLibraryState, type MealUserState } from './types'

export interface MealLibraryContextValue {
  state: MealLibraryState
  /** Live-connection phase for the meals subscription — drives the reconnecting banner. */
  phase: ConnectionPhase
  /** Manual resubscribe (the banner's "Try now"). */
  retry: () => void
}

export const MealLibraryContext = createContext<MealLibraryContextValue | undefined>(undefined)

interface MealLibraryProviderProps {
  children: ReactNode
}

// Whole-collection subscription over the durable flat meals collection, plus the sparse FE-owned
// meal_user_state. Both read-only here; writes go through services/firebase/mealUserState. This listener
// grows with library size — accepted, since type-ahead search needs the docs client-side anyway.
export function MealLibraryProvider({ children }: MealLibraryProviderProps) {
  const { uid } = useUserData()
  const [state, dispatch] = useReducer(mealLibraryReducer, initialMealLibraryState)

  // Drop the cache on sign-out; the subscriptions disable themselves via the null key.
  useEffect(() => {
    if (!uid) dispatch({ type: MealLibraryActionTypes.CLEAR })
  }, [uid])

  const { phase, retry } = useLiveSubscription<QuerySnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'mealLibrary',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(collection(db, 'users', activeUid, 'meals'), onData, onError),
    onData: (snapshot) => {
      const meals = snapshot.docs.map((d) => ({ ...d.data(), mealId: d.id }) as StoredMeal)
      dispatch({ type: MealLibraryActionTypes.SET_MEALS, payload: meals })
    }
  })

  // Secondary subscription — favorites/hidden state. Its connection phase is not
  // surfaced (the meals subscription owns the banner); a drop here just delays the
  // hearts, not the grid.
  useLiveSubscription<QuerySnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'mealUserState',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(collection(db, 'users', activeUid, 'meal_user_state'), onData, onError),
    onData: (snapshot) => {
      const userState: Record<string, MealUserState> = {}
      for (const d of snapshot.docs) userState[d.id] = { ...d.data(), mealId: d.id } as MealUserState
      dispatch({ type: MealLibraryActionTypes.SET_USER_STATE, payload: userState })
    }
  })

  return <MealLibraryContext.Provider value={{ state, phase, retry }}>{children}</MealLibraryContext.Provider>
}
